/**
 * Staking Dashboard Component
 * 
 * Displays $MANGO staking information, tier benefits, and actions.
 * Part of the Mango Group ecosystem.
 */

import { memo, useMemo } from "react";
import { useAccount } from "wagmi";
import {
  Coins,
  Shield,
  TrendingUp,
  Gift,
  ChevronRight,
  Sparkles,
  Lock,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useStaking, formatTokenAmount } from "@/hooks/useWeb3";
import { cn } from "@/lib/utils";

/**
 * Staking tier type
 */
type StakingTier = "none" | "bronze" | "silver" | "gold" | "platinum" | "diamond";

/**
 * Tier configuration with colors and icons
 */
const TIER_CONFIG: Record<StakingTier, {
  color: string;
  icon: string;
  gradient: string;
}> = {
  none: {
    color: "bg-gray-100 text-gray-600 border-gray-200",
    icon: "🔘",
    gradient: "from-gray-100 to-gray-200",
  },
  bronze: {
    color: "bg-amber-100 text-amber-700 border-amber-300",
    icon: "🥉",
    gradient: "from-amber-100 to-amber-200",
  },
  silver: {
    color: "bg-slate-200 text-slate-700 border-slate-300",
    icon: "🥈",
    gradient: "from-slate-100 to-slate-300",
  },
  gold: {
    color: "bg-yellow-100 text-yellow-700 border-yellow-300",
    icon: "🥇",
    gradient: "from-yellow-100 to-yellow-300",
  },
  platinum: {
    color: "bg-purple-100 text-purple-700 border-purple-300",
    icon: "💎",
    gradient: "from-purple-100 to-purple-300",
  },
  diamond: {
    color: "bg-cyan-100 text-cyan-700 border-cyan-300",
    icon: "👑",
    gradient: "from-cyan-100 to-cyan-300",
  },
};

/**
 * Staking tier thresholds (for progress calculation)
 */
const TIER_THRESHOLDS = {
  bronze: BigInt("100000000000000000000"), // 100
  silver: BigInt("500000000000000000000"), // 500
  gold: BigInt("1000000000000000000000"), // 1,000
  platinum: BigInt("5000000000000000000000"), // 5,000
  diamond: BigInt("10000000000000000000000"), // 10,000
};

interface StakingDashboardProps {
  className?: string;
}

/**
 * Main Staking Dashboard component
 */
export const StakingDashboard = memo(function StakingDashboard({
  className,
}: StakingDashboardProps) {
  const { isConnected } = useAccount();
  const { stakingInfo, tier: rawTier, totalStaked, benefits, nextTier, isLoading } = useStaking();

  // Type-safe tier
  const tier = (rawTier || "none") as StakingTier;

  // Calculate progress to next tier
  const progressToNextTier = useMemo(() => {
    if (!nextTier) return 100; // Already at max tier

    const current = BigInt(totalStaked);
    const required = BigInt(nextTier.required);
    
    if (required === BigInt(0)) return 0;
    
    const progress = Number((current * BigInt(100)) / required);
    return Math.min(progress, 100);
  }, [totalStaked, nextTier]);

  const tierConfig = TIER_CONFIG[tier];

  if (!isConnected) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Coins className="h-5 w-5 text-orange-500" />
            $MANGO Staking
          </CardTitle>
          <CardDescription>
            Connect your wallet to view staking benefits
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Stake $MANGO to unlock fee discounts, verified badges, and governance voting.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Coins className="h-5 w-5 text-orange-500" />
            $MANGO Staking
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-20 bg-gray-200 rounded-lg" />
            <div className="h-4 bg-gray-200 rounded w-3/4" />
            <div className="h-4 bg-gray-200 rounded w-1/2" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={cn("space-y-6", className)}>
      {/* Current Tier Card */}
      <Card className={cn("border-2", tierConfig.color)}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <span className="text-2xl">{tierConfig.icon}</span>
              <span className="capitalize">{tier} Tier</span>
            </CardTitle>
            <Badge variant="outline" className="text-lg px-3 py-1">
              {stakingInfo?.feeDiscount ?? 0}% off fees
            </Badge>
          </div>
          <CardDescription>
            {BigInt(totalStaked) > BigInt(0)
              ? `${formatTokenAmount(totalStaked)} $MANGO staked`
              : "No $MANGO staked yet"}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Progress to next tier */}
          {nextTier && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  Progress to {nextTier.name.charAt(0).toUpperCase() + nextTier.name.slice(1)}
                </span>
                <span className="font-mono">
                  {formatTokenAmount(totalStaked)} / {formatTokenAmount(nextTier.required)}
                </span>
              </div>
              <Progress value={progressToNextTier} className="h-2" />
              <p className="text-xs text-muted-foreground">
                Stake {formatTokenAmount(nextTier.difference)} more $MANGO to unlock{" "}
                {nextTier.discount}% fee discount
              </p>
            </div>
          )}

          {/* Benefits list */}
          {benefits.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-yellow-500" />
                Your Benefits
              </h4>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {benefits.map((benefit: string, i: number) => (
                  <li
                    key={i}
                    className="flex items-center gap-2 text-sm text-muted-foreground"
                  >
                    <ChevronRight className="h-3 w-3 text-green-500" />
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Total Staked */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-orange-100">
                <Lock className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Staked</p>
                <p className="text-2xl font-bold font-mono">
                  {formatTokenAmount(totalStaked)}
                </p>
                <p className="text-xs text-muted-foreground">$MANGO</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pending Rewards */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-green-100">
                <Gift className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending Rewards</p>
                <p className="text-2xl font-bold font-mono">
                  {stakingInfo?.positions?.[0]?.pendingRewards
                    ? formatTokenAmount(stakingInfo.positions[0].pendingRewards)
                    : "0"}
                </p>
                <p className="text-xs text-muted-foreground">$MANGO</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Fee Savings */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-purple-100">
                <TrendingUp className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Fee Discount</p>
                <p className="text-2xl font-bold">
                  {stakingInfo?.feeDiscount ?? 0}%
                </p>
                <p className="text-xs text-muted-foreground">on all fees</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Staking Actions</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-4">
          <Button className="gap-2">
            <Coins className="h-4 w-4" />
            Stake $MANGO
          </Button>
          <Button variant="outline" className="gap-2">
            <Lock className="h-4 w-4" />
            Unstake
          </Button>
          <Button
            variant="outline"
            className="gap-2"
            disabled={!stakingInfo?.positions?.[0]?.pendingRewards || 
              BigInt(stakingInfo?.positions?.[0]?.pendingRewards ?? "0") === BigInt(0)}
          >
            <Gift className="h-4 w-4" />
            Claim Rewards
          </Button>
        </CardContent>
      </Card>

      {/* Tier Comparison */}
      <TierComparison currentTier={tier} />
    </div>
  );
});

StakingDashboard.displayName = "StakingDashboard";

/**
 * Tier comparison table
 */
const TierComparison = memo(function TierComparison({
  currentTier,
}: {
  currentTier: string;
}) {
  const tiers = [
    { name: "Bronze", required: "100", discount: 25, benefits: ["25% fee discount", "Bronze badge"] },
    { name: "Silver", required: "500", discount: 40, benefits: ["40% fee discount", "Silver badge", "Priority support"] },
    { name: "Gold", required: "1,000", discount: 50, benefits: ["50% fee discount", "Gold badge", "Governance voting"] },
    { name: "Platinum", required: "5,000", discount: 60, benefits: ["60% fee discount", "Featured seller", "Early access"] },
    { name: "Diamond", required: "10,000", discount: 75, benefits: ["75% fee discount", "Revenue share", "All benefits"] },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Staking Tiers
        </CardTitle>
        <CardDescription>
          Stake more $MANGO to unlock additional benefits
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-2">Tier</th>
                <th className="text-left py-3 px-2">Required</th>
                <th className="text-left py-3 px-2">Discount</th>
                <th className="text-left py-3 px-2">Key Benefits</th>
              </tr>
            </thead>
            <tbody>
              {tiers.map((t) => {
                const isCurrentTier = t.name.toLowerCase() === currentTier;
                const tierKey = t.name.toLowerCase() as keyof typeof TIER_CONFIG;
                const config = TIER_CONFIG[tierKey];

                return (
                  <tr
                    key={t.name}
                    className={cn(
                      "border-b last:border-0",
                      isCurrentTier && "bg-orange-50"
                    )}
                  >
                    <td className="py-3 px-2">
                      <div className="flex items-center gap-2">
                        <span>{config.icon}</span>
                        <span className="font-medium">{t.name}</span>
                        {isCurrentTier && (
                          <Badge variant="outline" className="text-xs">
                            Current
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-2 font-mono">{t.required} $MANGO</td>
                    <td className="py-3 px-2">
                      <Badge variant="secondary">{t.discount}% off</Badge>
                    </td>
                    <td className="py-3 px-2 text-muted-foreground">
                      {t.benefits.slice(0, 2).join(", ")}
                      {t.benefits.length > 2 && "..."}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
});

TierComparison.displayName = "TierComparison";

export default StakingDashboard;
